/**
 * ValidationX.js
 * Validation rules for Admission Application Form (1st to 10th)
 */

// ==================== HELPER VALIDATORS ====================

// Tamil-only validation (Unicode range for Tamil: \u0B80-\u0BFF)
const isTamilOnly = (text) => {
  if (!text) return false;
  const tamilRegex = /^[\u0B80-\u0BFF\s.,\-()0-9]+$/;
  return tamilRegex.test(text.trim());
};

// Email validation
export const isValidEmail = (email) => {
  const trimmed = email.trim();
  if (/[^a-zA-Z0-9.@]/.test(trimmed)) {
    return {
      valid: false,
      message:
        "Sorry, only letters (a-z), numbers (0-9), and periods (.) are allowed",
    };
  }
  if (/\.@/.test(trimmed)) {
    return {
      valid: false,
      message:
        "Sorry, the last character before @ must be a letter (a-z) or number (0-9)",
    };
  }
  if (!/^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
    return {
      valid: false,
      message: "Please enter a valid email address",
    };
  }
  return { valid: true, message: "" };
};

const isValidMobile = (mobile) => {
  const mobileRegex = /^[6-9]\d{9}$/;
  return mobileRegex.test(mobile);
};

const isValidAadhar = (aadhar) => {
  const aadharRegex = /^\d{12}$/;
  return aadharRegex.test(aadhar);
};

const isValidPincode = (pincode) => {
  const pincodeRegex = /^\d{6}$/;
  return pincodeRegex.test(pincode);
};

const isValidEmisNo = (emis) => {
  if (!emis) return false;
  const emisRegex = /^\d{1,10}$/;
  return emisRegex.test(emis);
};

// Helper to get value from object or plain value
const getVal = (field) => {
  if (!field) return null;
  if (typeof field === "object" && field.value !== undefined)
    return field.value;
  return field;
};

// ==================== MAIN VALIDATION FUNCTION ====================
export const validateAdmissionForm = (formData, translations) => {
  const errors = {};
  const t = translations?.labels || {};

  // ---- Basic Details ----
  if (!formData.student_name_in_tamil?.trim()) {
    errors.student_name_in_tamil =
      t?.studentNameTamilRequired || "Student's Name (Tamil) is required";
  } else if (!isTamilOnly(formData.student_name_in_tamil)) {
    errors.student_name_in_tamil =
      t?.onlyTamilAllowed || "Please enter only Tamil characters";
  }

  if (!formData.student_name?.trim()) {
    errors.student_name =
      t?.studentNameEnglishRequired || "Student's Name (English) is required";
  }

  if (!formData.date_of_birth) {
    errors.date_of_birth =
      t?.dateOfBirthRequired || "Date of birth is required";
  } else {
    const today = new Date();
    const dob = new Date(formData.date_of_birth);

    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    if (age < 3) {
      errors.date_of_birth =
        t?.dateOfBirthMinAge || "Age must be at least 3 years";
    }
  }

  if (formData.aadhar_no && !isValidAadhar(formData.aadhar_no)) {
    errors.aadhar_no =
      t?.aadharNumberInvalid || "Enter valid 12-digit Aadhar number";
  }

  if (!formData.email?.trim()) {
    errors.email = t?.emailRequired || "Email is required";
  } else {
    const emailResult = isValidEmail(formData.email);
    if (!emailResult.valid) {
      errors.email = emailResult.message;
    }
  }

  if (!formData.mobile_number?.trim()) {
    errors.mobile_number =
      t?.mobileNumberRequired || "Mobile number is required";
  } else if (!isValidMobile(formData.mobile_number)) {
    errors.mobile_number =
      t?.mobileNumberInvalid || "Enter valid 10-digit mobile number";
  }

  if (!getVal(formData.gender)) {
    errors.gender = t?.genderRequired || "Gender is required";
  }

  if (!getVal(formData.religion)) {
    errors.religion = t?.religionRequired || "Religion is required";
  }

  if (!formData.caste?.trim()) {
    errors.caste = t?.casteRequired || "Caste is required";
  }

  if (!getVal(formData.community)) {
    errors.community = t?.categoryRequired || "Category is required";
  }

  if (!formData.nationality?.trim()) {
    errors.nationality = t?.nationalityRequired || "Nationality is required";
  }

  if (!getVal(formData.student_native_state)) {
    errors.student_native_state = t?.stateRequired || "State is required";
  }

  // ---- Residential Details ----
  const livingWith = getVal(formData.living_with);

  if (!livingWith) {
    errors.living_with = t?.livingWithRequired || "This field is required";
  }

  if (livingWith === "parent") {
    if (!formData.parent_name?.trim()) {
      errors.parent_name = t?.parentNameRequired || "Name is required";
    }
    if (!getVal(formData.parent_occupation)) {
      errors.parent_occupation =
        t?.occupationRequired || "Occupation is required";
    }
    if (!getVal(formData.parent_annual_salary)) {
      errors.parent_annual_salary =
        t?.incomeRequired || "Annual Income is required";
    }
    if (!formData.parent_address?.trim()) {
      errors.parent_address = t?.addressRequired || "Address is required";
    }
    if (!formData.parent_city?.trim()) {
      errors.parent_city = t?.cityRequired || "City is required";
    }
    if (!formData.parent_district?.trim()) {
      errors.parent_district = t?.districtRequired || "District is required";
    }
    if (!getVal(formData.parent_state)) {
      errors.parent_state = t?.stateRequired || "State is required";
    }
    if (!formData.parent_pincode?.trim()) {
      errors.parent_pincode = t?.pincodeRequired || "Pincode is required";
    } else if (!isValidPincode(formData.parent_pincode)) {
      errors.parent_pincode =
        t?.pincodeInvalid || "Enter valid 6-digit pincode";
    }
  }

  if (livingWith === "guardian") {
    if (!formData.guardian_name?.trim()) {
      errors.guardian_name = t?.guardianNameRequired || "Name is required";
    }
    if (!getVal(formData.guardian_occupation)) {
      errors.guardian_occupation =
        t?.occupationRequired || "Occupation is required";
    }
    if (!getVal(formData.guardian_annual_salary)) {
      errors.guardian_annual_salary =
        t?.incomeRequired || "Annual Income is required";
    }
    if (!formData.guardian_address?.trim()) {
      errors.guardian_address = t?.addressRequired || "Address is required";
    }
    if (!formData.guardian_city?.trim()) {
      errors.guardian_city = t?.cityRequired || "City is required";
    }
    if (!formData.guardian_district?.trim()) {
      errors.guardian_district = t?.districtRequired || "District is required";
    }
    if (!getVal(formData.guardian_state)) {
      errors.guardian_state = t?.stateRequired || "State is required";
    }
    if (!formData.guardian_pincode?.trim()) {
      errors.guardian_pincode = t?.pincodeRequired || "Pincode is required";
    } else if (!isValidPincode(formData.guardian_pincode)) {
      errors.guardian_pincode =
        t?.pincodeInvalid || "Enter valid 6-digit pincode";
    }
  }

  // ---- Last Studied Details ----
  if (!getVal(formData.last_studied_class)) {
    errors.last_studied_class =
      t?.lastStudiedClassRequired || "Last Studied Class is required";
  }

  if (!formData.last_studied_school?.trim()) {
    errors.last_studied_school =
      t?.lastStudiedSchoolRequired || "Last Studied School is required";
  }

  // EMIS No - mandatory, numbers only, max 10
  if (!formData.emis_id?.trim()) {
    errors.emis_id = t?.emisNoRequired || "EMIS No is required";
  } else if (!isValidEmisNo(formData.emis_id)) {
    errors.emis_id =
      t?.emisNoInvalid || "Enter a valid EMIS No (numbers only, max 10 digits)";
  }

  if (!formData.document_path && formData.attached_document_type) {
    errors.document_path = t?.documentRequired || "Document is required";
  }

  // ---- Preferred Course Details ----
  if (!getVal(formData.preferred_class)) {
    errors.preferred_class =
      t?.preferredClassRequired || "Preferred Class for Admission is required";
  }

  if (!getVal(formData.mother_tongue)) {
    errors.mother_tongue =
      t?.motherTongueRequired || "Mother Tongue is required";
  }

  if (!getVal(formData.preferred_first_language)) {
    errors.preferred_first_language =
      t?.preferredFirstLanguageRequired ||
      "Preferred First Language to Study is required";
  }

  // ---- Previous School Address ----
  if (!formData.previous_school_address_1?.trim()) {
    errors.previous_school_address_1 =
      t?.schoolNameRequired || "School Name 1 is required";
  }

  if (!getVal(formData.previous_studied_class_1)) {
    errors.previous_studied_class_1 = t?.classRequired || "Class is required";
  }

  if (!getVal(formData.previous_studied_year_1)) {
    errors.previous_studied_year_1 =
      t?.yearOfStudyRequired || "Year of Study is required";
  }

  // ---- Identification Marks ----
  if (!formData.identification_mark_1?.trim()) {
    errors.identification_mark_1 =
      t?.identificationMark1Required || "Identification Mark 1 is required";
  }

  if (!formData.identification_mark_2?.trim()) {
    errors.identification_mark_2 =
      t?.identificationMark2Required || "Identification Mark 2 is required";
  }

  // ---- Other Details ----
  if (!formData.place?.trim()) {
    errors.place = t?.placeRequired || "Place is required";
  }

  if (!formData.student_image) {
    errors.student_image =
      t?.studentImageRequired || "Student Image is required";
  }

  return errors;
};

// ==================== OTP VALIDATION ====================
export const validateOtp = (otp, translations) => {
  const t = translations?.labels || {};
  if (!otp?.trim()) {
    return t?.otpRequired || "OTP is required";
  }
  if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
    return t?.otpInvalid || "Enter a valid 6-digit OTP";
  }
  return "";
};

export default validateAdmissionForm;
